import { Component, Inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Observable, BehaviorSubject } from 'rxjs';
import { map, shareReplay, switchMap, scan } from 'rxjs/operators';

import { CoreApiService } from '../../core-api.service';
import { pluralize } from '../../shared/container.utils';
import { ContainerType } from '../../shared/enums/container-type.enum';
import {
  ContainerChildren,
  RelatedContainer,
  SuggestedFile,
  GearSuggestions,
} from '../../shared/models/gear-suggestions.model';

@Component({
  selector: 'file-browser-dialog',
  templateUrl: './file-browser-dialog.component.html',
  styles: [`
    :host {
      display: block;
      overflow: hidden;
      position: relative;
      width: 600px;
    }
    .close-button {
      color: #cccccc;
      position: absolute;
      right: -4px;
      top: -4px;
    }
    .load-more-button {
      width: 100%;
      margin: 0 !important;
    }
    .modal-title {
      display: flex;
      width: calc(100% - 20px);
      margin: 0;
    }
    .modal-title > span {
      display: flex;
      align-items: center;
      white-space: nowrap;
    }
    .modal-title > .enable-ellipsis {
      min-width: 0;
    }
    .modal-title > span > .ellipsis-block {
      padding-right: 0;
    }
    .modal-title > span > span.fa {
      margin: 0 4px;
    }
    [mat-dialog-content] {
      margin: 0;
      min-height: 440px;
      padding: 0;
    }
    .pull-left {
      float: left;
    }
    .modal-footer {
      text-align: right;
    }
    fw-icon {
      vertical-align: -26%;
    }
    .suggested-icon {
      margin-right: -8px;
    }
    .position-right {
      position: absolute;
      right: 4px;
      top: 0;
      padding: 4px;
      &:hover {
        background: #d0d6e1;
        color: #8191b0;
        cursor: pointer;
      }
    }
    button.btn:disabled {
      cursor: not-allowed;
      opacity: .65;
    }
  `],
  host: {
    class: 'browser-modal',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FileBrowserDialog implements OnInit {
  private collectionId = this.data.collectionId
  private containerId = this.data.containerId
  private containerType = this.data.containerType
  public gearContext = this.data.gearContext
  public inputType = this.data.inputType // used in the template
  public isOutput = this.data.isOutput
  private gearId = this.data.gearId

  public parents$: Observable<(RelatedContainer & { useEllipsis?: boolean })[]>
  public children$: Observable<ChildrenSummary>
  public files$: Observable<SuggestedFileOption[]>
  public selectedInput: SuggestedFileOption
  public selectedOutput: RelatedContainer

  public childrenPage$: BehaviorSubject<number>
  public filesPage$: BehaviorSubject<number>
  readonly pageSize = 100

  constructor(
    @Inject(MAT_DIALOG_DATA) private data: {
      collectionId: string,
      containerId: string,
      containerType: string,
      gearContext: string,
      inputType: string,
      isOutput: boolean,
      gearId: string,
    },
    private dialogRef: MatDialogRef<FileBrowserDialog, SuggestedFileOption | RelatedContainer>,
    private coreApi: CoreApiService,
  ) { }

  ngOnInit(): void {
    this.containerLevel(this.containerType, this.containerId);
  }

  containerLevel(containerType: string, containerId: string): void {
    this.selectedInput = null;
    this.selectedOutput = null;
    const params = this.collectionId ? { collectionId: this.collectionId } : {};

    this.childrenPage$ = new BehaviorSubject(1);
    const suggestions$ = this.childrenPage$.pipe(
      switchMap(page =>
        this.coreApi.gears.suggest(this.gearId, pluralize(containerType), containerId, {
          ...params,
          include: 'children',
          limit: this.pageSize,
          page: page,
        })
      ),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
    this.parents$ = suggestions$.pipe(
      map(level => {
        const parentsByType: { [cont_type in ContainerType]?: RelatedContainer } = level.parents.reduce((result, parent) => {
          result[parent.cont_type] = parent;
          return result;
        }, {});
        // if you want something ordered right, you have to do it yourself
        const parents = [
          parentsByType.collection,
          parentsByType.group,
          parentsByType.project,
          parentsByType.subject,
          parentsByType.session,
          parentsByType.acquisition,
          parentsByType.analysis,
        ].filter(value => value);
        return [
          ...parents.map(parent => ({ ...parent, useEllipsis: (parent.label || parent._id).length > 24 })),
          {
            _id: level._id,
            cont_type: level.cont_type,
            label: level.label,
            useEllipsis: (level.label || level._id).length > 24,
          },
        ];
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
    this.children$ = suggestions$.pipe(
      scan((children: ChildrenSummary, suggestions: GearSuggestions) => {
        // add new containers to each respective list
        for (const [level, containers] of Object.entries(suggestions.children)) {
          children[level] = (children[level] || []).concat(containers);
        }
        // recalculate the current total
        children.total = Object.entries(children).reduce((total, [level, containers]) => {
          return level === 'total' ? total : total + containers.length;
        }, 0);
        return children;
      }, { total: 0 }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    if (!this.isOutput) {
      this.filesPage$ = new BehaviorSubject(1);
      this.files$ = this.filesPage$.pipe(
        switchMap(page =>
          this.coreApi.gears.suggest(this.gearId, pluralize(containerType), containerId, {
            ...params,
            include: 'files',
            limit: this.pageSize,
            page: page,
          })
        ),
        scan((files: SuggestedFileOption[], suggestions: GearSuggestions) => {
          return files.concat(suggestions.files.map(file => ({
            container_id: suggestions._id,
            container_name: suggestions.label,
            container_type: suggestions.cont_type,
            file: file,
          })));
        }, []),
        shareReplay({ bufferSize: 1, refCount: true }),
      );
    }
  }

  submitSelection(): void {
    this.dialogRef.close(this.isOutput ? this.selectedOutput : this.selectedInput);
  }
}

export interface SuggestedFileOption {
  container_id: string
  container_type: string
  file: SuggestedFile
}

interface ChildrenSummary extends ContainerChildren {
  total: number
}
